import { computed, inject, Injectable, signal } from '@angular/core';
import { Params } from '@angular/router';
import { CompanyService } from './company.service';
import { AppId, ChainFacets } from '../../model';

const EMPTY: ChainFacets = { years: [], suppliers: [], tags: [] };

/**
 * The one place the chain-list facets live, so the list and the side navigation cannot show
 * different year counts for the same site.
 *
 * Two entry points, because the two consumers learn about a site at different moments:
 *
 * - `resolve()` is the list's route resolver. It runs on EVERY navigation to the list
 *   (`runGuardsAndResolvers: 'always'`), so the navigation's counts follow the list's own filter
 *   context and stay correct after a chain is created or deleted — without a second request.
 * - `ensure()` is called when the selected site changes. A user who lands on Evaluations first
 *   has never resolved anything, so the navigation fetches the unfiltered facets once itself.
 */
@Injectable({ providedIn: 'root' })
export class ChainFacetsService {
  private readonly companyService = inject(CompanyService);
  private readonly _facets = signal<ChainFacets>(EMPTY);
  private siteId?: AppId;
  /**
   * `ensure` and `resolve` can be in flight at the same time during a site switch. The counter
   * decides which answer is allowed to land: only the newest request may publish, so a slow
   * unfiltered fetch can never overwrite the fresher filtered one it raced.
   */
  private revision = 0;

  /** The year axis of the current facets — the only one the navigation renders. */
  readonly years = computed(() => this._facets().years);

  /** Route-resolver entry point: fetches, publishes, and hands the result on unchanged. */
  async resolve(siteId: AppId, queryParams: Params): Promise<ChainFacets> {
    const stamp = this.claim(siteId);
    const facets = await this.companyService.chainFacetsForSite(siteId, queryParams);
    this.publish(stamp, facets);
    return facets;
  }

  /** Loads the facets once for a site the list has not resolved. */
  async ensure(siteId: AppId): Promise<void> {
    if (this.siteId === siteId) {
      return;
    }
    const stamp = this.claim(siteId);
    this.publish(stamp, await this.companyService.chainFacetsForSite(siteId, {}));
  }

  /**
   * Claims the site before the request goes out: a burst of site emissions must produce one
   * request, and another company's years must leave the navigation at once rather than linger
   * as links into chains this user cannot see.
   */
  private claim(siteId: AppId): number {
    if (this.siteId !== siteId) {
      this._facets.set(EMPTY);
    }
    this.siteId = siteId;
    return ++this.revision;
  }

  private publish(stamp: number, facets: ChainFacets) {
    if (stamp === this.revision) {
      this._facets.set(facets);
    }
  }
}
